import React from 'react'
import PropTypes from 'prop-types'
import { withStyles } from '@material-ui/core/styles'
import pbicon from './../../assets/PB.png'

const styles = () => ({
  root: {
    width: '100%',
    maxWidth: 290,
    display: 'flex',
    flexDirection: 'column',
    background: '#FFFFFF',
    borderRadius: 12,
    overflow: 'hidden',
    boxShadow: '0px 4px 79px rgba(0, 0, 0, 0.10)',
    cursor: 'pointer',
    transition: 'transform .18s ease, box-shadow .18s ease',
    '&:hover': {
      transform: 'translateY(-2px)',
      boxShadow: '0px 8px 32px rgba(0, 0, 0, 0.14)',
    },
  },

  thumbWrap: {
    position: 'relative',
    width: '100%',
    aspectRatio: '16 / 9',
    overflow: 'hidden',
    background: '#0E2D54',

    '&:hover $shade, &:focus-within $shade': { opacity: 0.42 },
    '&:hover $playBtn, &:focus-within $playBtn': { transform: 'scale(1.06) translateZ(0)' },
  },

  thumbImg: { width: '100%', height: '100%', objectFit: 'cover', display: 'block' },

  shade: {
    position: 'absolute',
    inset: 0,
    background: '#000',
    pointerEvents: 'none',
    transition: 'opacity .18s ease',
    opacity: 0.18,
  },

  playCenter: {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    display: 'grid',
    placeItems: 'center',
    pointerEvents: 'none',
  },

  playBtn: {
    pointerEvents: 'auto',
    width: 48,
    height: 48,
    border: 0,
    borderRadius: '50%',
    background: '#0C70E8E5',
    boxShadow: '0px 4px 4px rgba(0,0,0,0.15)',
    display: 'grid',
    placeItems: 'center',
    cursor: 'pointer',
    transform: 'scale(1) translateZ(0)',
    transformOrigin: 'center',
    transition: 'transform .18s ease',
    willChange: 'transform',
    backfaceVisibility: 'hidden',
    '&:focus-visible': { outline: '2px solid #fff', outlineOffset: 3, boxShadow: '0 0 0 5px rgba(12,112,232,.9)' },
  },

  playIcon: { width: 20, height: 20, fill: '#fff', transform: 'translateX(1px)' },

  info: {
    minWidth: 0,
    padding: '12px 16px 16px',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    gap: 6,
  },

  chip: {
    padding: '2px 8px',
    borderRadius: 6,
    background: '#E3F2FF',
    color: '#0C70E8',
    fontFamily: 'var(--font-sans)',
    fontWeight: 600,
    fontSize: 11,
    lineHeight: '16px',
    letterSpacing: '0.01em',
    textTransform: 'none',
  },

  title: {
    margin: 0,
    fontFamily: 'var(--font-sans)',
    fontWeight: 600,
    fontSize: 16,
    lineHeight: '24px',
    letterSpacing: '-0.02em',
    color: '#002857',
  },
  meta: {
    fontFamily: 'var(--font-sans)',
    fontWeight: 400,
    fontSize: 13,
    lineHeight: '18px',
    color: '#305178',
  },
})

function getYoutubeId(url) {
  if (!url) return null
  try {
    const u = new URL(url)
    if (u.hostname.includes('youtu.be')) return u.pathname.slice(1)
    const v = u.searchParams.get('v')
    if (v) return v
    const m = u.pathname.match(/\/embed\/([0-9A-Za-z_-]{11})/)
    if (m) return m[1]
  } catch {}
  const m2 = String(url).match(/(?:v=|\/embed\/|youtu\.be\/)([0-9A-Za-z_-]{11})/)
  return m2 ? m2[1] : null
}
function thumbFromData(data) {
  const direct =
    data?.thumbnail ||
    data?.thumbnails?.maxres?.url ||
    data?.thumbnails?.high?.url ||
    data?.thumbnails?.medium?.url
  if (direct) return direct
  const id = getYoutubeId(data?.url)
  return id ? `https://img.youtube.com/vi/${id}/hqdefault.jpg` : pbicon
}
function formatDuration(totalSeconds = 0) {  const s = Math.max(0, Number(totalSeconds) || 0)
  const m = Math.floor(s / 60)
  const sec = String(Math.floor(s % 60)).padStart(2, '0')
  return `${m}:${sec}`
}

function ItemVideo({ classes, data, configData }) {
  const { title, product, duration, steps, url } = data || {}
  const { anchorLinked, onclickedPbIcon } = configData || {}
  const thumb = thumbFromData(data)
  const open = () => typeof onclickedPbIcon === 'function' && onclickedPbIcon(url)

  const stepCount = Number(steps) || 0
  const hasSteps = stepCount > 0
  const hasDuration = Number(duration) > 0
  const isDemo = hasSteps || !hasDuration
  const kind = isDemo ? 'Interactive Demo' : 'Video Guides'
  const metadata = hasSteps
    ? `${product}${product ? ' | ' : ''}${stepCount} ${stepCount === 1 ? 'Step' : 'Steps'}`
    : `${product}${product && hasDuration ? ' | ' : ''}${hasDuration ? formatDuration(duration) : ''}`

  const Card = (
    <>
      <div className={classes.thumbWrap}>
        <img className={classes.thumbImg} src={thumb} alt={title || 'video'} loading="lazy" decoding="async" />
        <span className={classes.shade} aria-hidden="true" />
        <div className={classes.playCenter}>
          <button type="button" className={classes.playBtn}
            aria-label={`${kind === 'Video Guides' ? 'Play' : 'Open'} ${title || kind}`}>
            <svg viewBox="0 0 24 24" className={classes.playIcon}><path d="M8 5v14l11-7z" /></svg>
          </button>
        </div>
      </div>
      <div className={classes.info}>
        <span className={classes.chip}>{kind}</span>
        <h3 className={classes.title}>{title}</h3>
        <div className={classes.meta}>{metadata}</div>
      </div>
    </>
  )

  // Mouse clicks anywhere on the card open it; keyboard users get a single tab
  // stop (the play button), whose click bubbles up to this handler.
  return (
    <div className={classes.root} onClick={open}>
      {anchorLinked ? (
        <a href={url} target="_blank" rel="noopener noreferrer"
          style={{ textDecoration: 'none' }} onClick={(e) => e.stopPropagation()}>
          {Card}
        </a>
      ) : (
        Card
      )}
    </div>
  )
}

ItemVideo.defaultProps = {
  data: { title: '', duration: 0, url: '', category: '', product: '' },
  configData: { anchorLinked: false, onclickedPbIcon: null },
}
ItemVideo.propTypes = { data: PropTypes.object.isRequired, configData: PropTypes.object }

export default withStyles(styles)(ItemVideo)
